/**
 * Rax Model Picker Component
 *
 * Allows users to select their preferred AI model from the dashboard.
 * Shows cost estimates per model.
 */

import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { raxApi } from "../../api/rax-api.js";
import { raxTheme } from "../styles/rax-theme.js";

interface ModelOption {
  id: string;
  name: string;
  provider: string;
  costPer1kTokens: number; // average of input+output
  description: string;
  recommended?: boolean;
  tier: "budget" | "balanced" | "premium";
}

const MODELS: ModelOption[] = [
  {
    id: "claude-3-5-haiku",
    name: "Claude 3.5 Haiku",
    provider: "Anthropic",
    costPer1kTokens: 0.00075,
    description: "Fast and affordable. Great for quick tasks.",
    tier: "budget",
  },
  {
    id: "gpt-4o-mini",
    name: "GPT-4o Mini",
    provider: "OpenAI",
    costPer1kTokens: 0.000375,
    description: "Budget-friendly with good quality.",
    tier: "budget",
  },
  {
    id: "claude-sonnet-4",
    name: "Claude Sonnet 4",
    provider: "Anthropic",
    costPer1kTokens: 0.009,
    description: "Best balance of quality and cost.",
    recommended: true,
    tier: "balanced",
  },
  {
    id: "gpt-5.2",
    name: "GPT-5.2",
    provider: "OpenAI",
    costPer1kTokens: 0.007875,
    description: "Strong reasoning and coding for demanding tasks.",
    tier: "balanced",
  },
  {
    id: "claude-opus-4",
    name: "Claude Opus 4",
    provider: "Anthropic",
    costPer1kTokens: 0.045,
    description: "Maximum intelligence. Best for complex tasks.",
    tier: "premium",
  },
  {
    id: "o1",
    name: "o1",
    provider: "OpenAI",
    costPer1kTokens: 0.0375,
    description: "Advanced reasoning capabilities.",
    tier: "premium",
  },
];

@customElement("rax-model-picker")
export class RaxModelPicker extends LitElement {
  static override styles = [
    raxTheme,
    css`
      :host {
        display: block;
      }
      
      .picker-container {
        padding: var(--space-6);
      }
      
      .provider-notice {
        margin-bottom: var(--space-4);
        padding: var(--space-3) var(--space-4);
        border-radius: var(--radius-md);
        border: 1px solid rgba(255, 208, 0, 0.5);
        background: rgba(255, 208, 0, 0.08);
        color: var(--text-primary);
        font-size: var(--font-sm);
        line-height: 1.45;
      }
      
      .helper-text {
        margin-top: var(--space-3);
        font-size: var(--font-xs);
        color: var(--text-tertiary);
      }
      
      .helper-text.error {
        color: var(--error);
      }
      
      .picker-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--space-5);
      }
      
      .picker-title {
        font-size: var(--font-lg);
        font-weight: 700;
        color: var(--text-primary);
      }
      
      .current-model {
        font-size: var(--font-sm);
        color: var(--text-secondary);
        padding: var(--space-2) var(--space-3);
        background: var(--bg-elevated);
        border-radius: var(--radius-md);
        border: 1px solid var(--border-color);
      }
      
      .tier-section {
        margin-bottom: var(--space-5);
      }
      
      .tier-label {
        font-size: var(--font-xs);
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: var(--text-tertiary);
        margin-bottom: var(--space-3);
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }
      
      .tier-label .badge {
        font-size: 10px;
        padding: 1px 6px;
        border-radius: var(--radius-full);
        font-weight: 500;
      }
      
      .tier-label .badge.budget {
        background: #22c55e20;
        color: #22c55e;
      }
      
      .tier-label .badge.balanced {
        background: #3b82f620;
        color: #3b82f6;
      }
      
      .tier-label .badge.premium {
        background: #f59e0b20;
        color: #f59e0b;
      }
      
      .model-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
        gap: var(--space-3);
      }
      
      .model-card {
        padding: var(--space-4);
        background: var(--bg-elevated);
        border: 2px solid var(--border-color);
        border-radius: var(--radius-lg);
        cursor: pointer;
        transition: all var(--transition-fast);
        position: relative;
      }
      
      .model-card:hover {
        border-color: var(--rax-yellow);
        background: rgba(255, 214, 0, 0.03);
      }
      
      .model-card.selected {
        border-color: var(--rax-yellow);
        background: rgba(255, 214, 0, 0.08);
        box-shadow: 0 0 15px var(--accent-glow);
      }
      
      .model-card .recommended {
        position: absolute;
        top: -8px;
        right: 12px;
        font-size: 10px;
        padding: 2px 8px;
        background: var(--rax-yellow);
        color: var(--rax-black);
        border-radius: var(--radius-full);
        font-weight: 700;
      }
      
      .model-name {
        font-weight: 600;
        font-size: var(--font-md);
        color: var(--text-primary);
        margin-bottom: var(--space-1);
      }
      
      .model-provider {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
        margin-bottom: var(--space-2);
      }
      
      .model-description {
        font-size: var(--font-sm);
        color: var(--text-secondary);
        line-height: 1.4;
        margin-bottom: var(--space-3);
      }
      
      .model-cost {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }
      
      .cost-estimate {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
      }
      
      .cost-value {
        font-weight: 600;
        color: var(--rax-yellow);
      }
      
      .save-btn {
        margin-top: var(--space-5);
        padding: var(--space-3) var(--space-6);
        background: var(--rax-yellow);
        color: var(--rax-black);
        border: none;
        border-radius: var(--radius-md);
        font-weight: 700;
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .save-btn:hover {
        background: var(--rax-gold);
        box-shadow: var(--shadow-glow);
      }
      
      .save-btn:disabled {
        opacity: 0.5;
        cursor: default;
      }
      
      @media (max-width: 900px) {
        .picker-container {
          padding: var(--space-4);
        }
      
        .picker-header {
          flex-direction: column;
          align-items: flex-start;
          gap: var(--space-2);
        }
      }
      
      @media (max-width: 640px) {
        .picker-container {
          padding: var(--space-3);
        }
      
        .model-grid {
          grid-template-columns: minmax(0, 1fr);
        }
      
        .save-btn {
          width: 100%;
        }
      }
    `,
  ];

  @property({ type: String })
  selectedModel = "claude-sonnet-4";

  @state()
  private saving = false;

  @state()
  private initializing = false;

  @state()
  private providerSelectionRequired = false;

  @state()
  private helperMessage: string | null = null;

  @state()
  private helperError: string | null = null;

  override connectedCallback() {
    super.connectedCallback();
    void this.loadInitialState();
  }

  override render() {
    const tiers: Array<{ key: string; label: string; models: ModelOption[] }> = [
      { key: "budget", label: "💚 Budget", models: MODELS.filter((m) => m.tier === "budget") },
      {
        key: "balanced",
        label: "💙 Balanced",
        models: MODELS.filter((m) => m.tier === "balanced"),
      },
      { key: "premium", label: "🧡 Premium", models: MODELS.filter((m) => m.tier === "premium") },
    ];

    return html`
      <div class="picker-container">
        ${
          this.providerSelectionRequired
            ? html`
                <div class="provider-notice">
                  Multiple BYOK providers are configured. Pick your default model now so runtime routing is
                  explicit.
                </div>
              `
            : ""
        }
        <div class="picker-header">
          <div class="picker-title">🤖 Choose Your Model</div>
          <div class="current-model">
            Current: ${MODELS.find((m) => m.id === this.selectedModel)?.name ?? this.selectedModel}
          </div>
        </div>

        ${tiers.map(
          (tier) => html`
          <div class="tier-section">
            <div class="tier-label">
              ${tier.label}
              <span class="badge ${tier.key}">${tier.key}</span>
            </div>
            <div class="model-grid">
              ${tier.models.map(
                (model) => html`
                <div
                  class="model-card ${this.selectedModel === model.id ? "selected" : ""}"
                  @click=${() => this.selectModel(model.id)}
                >
                  ${
                    model.recommended
                      ? html`
                          <span class="recommended">★ Recommended</span>
                        `
                      : ""
                  }
                  <div class="model-name">${model.name}</div>
                  <div class="model-provider">${model.provider}</div>
                  <div class="model-description">${model.description}</div>
                  <div class="model-cost">
                    <span class="cost-estimate">Est. per message:</span>
                    <span class="cost-value">~$${this.estimatePerMessage(model)}</span>
                  </div>
                </div>
              `,
              )}
            </div>
          </div>
        `,
        )}

        <button
          class="save-btn"
          ?disabled=${this.saving || this.initializing}
          @click=${this.savePreference}
        >
          ${this.saving ? "Saving..." : this.initializing ? "Loading..." : "⚡ Save Model Preference"}
        </button>
        ${this.helperError ? html`<div class="helper-text error">${this.helperError}</div>` : ""}
        ${this.helperMessage ? html`<div class="helper-text">${this.helperMessage}</div>` : ""}
      </div>
    `;
  }

  private selectModel(modelId: string) {
    this.selectedModel = modelId;
    this.dispatchEvent(
      new CustomEvent("model-changed", {
        bubbles: true,
        detail: { modelId },
      }),
    );
  }

  private estimatePerMessage(model: ModelOption): string {
    // Assume ~2k tokens per message (1k in, 1k out)
    const cost = model.costPer1kTokens * 2;
    if (cost < 0.001) return cost.toFixed(4);
    if (cost < 0.01) return cost.toFixed(3);
    return cost.toFixed(2);
  }

  private async savePreference() {
    this.saving = true;
    this.helperError = null;
    this.helperMessage = null;
    try {
      const result = await raxApi.setPreferredModel(this.selectedModel);
      if (!result.success) {
        throw new Error(result.error || "Save failed");
      }
      this.providerSelectionRequired = false;
      this.helperMessage = "Default model updated.";
      this.dispatchEvent(
        new CustomEvent("model-saved", {
          bubbles: true,
          detail: { modelId: this.selectedModel },
        }),
      );
    } catch (err) {
      console.error("[ModelPicker] Save failed:", err);
      this.helperError = err instanceof Error ? err.message : "Failed to save model preference";
    } finally {
      this.saving = false;
    }
  }

  private normalizeKnownModel(rawModel?: string | null): string | null {
    if (!rawModel) {
      return null;
    }
    const trimmed = rawModel.trim();
    if (!trimmed) {
      return null;
    }

    const direct = MODELS.find((model) => model.id === trimmed);
    if (direct) {
      return direct.id;
    }

    const lower = trimmed.toLowerCase();
    if (lower.includes("gpt-5.2")) {
      return "gpt-5.2";
    }
    if (lower.includes("gpt-4o-mini")) {
      return "gpt-4o-mini";
    }
    if (lower.includes("gpt-4o")) {
      return "gpt-4o";
    }
    if (lower.includes("claude-opus-4")) {
      return "claude-opus-4";
    }
    if (lower.includes("claude-sonnet-4")) {
      return "claude-sonnet-4";
    }
    if (lower.includes("claude-3-5-haiku")) {
      return "claude-3-5-haiku";
    }
    if (lower.endsWith("/o1") || lower === "o1") {
      return "o1";
    }
    if (lower.includes("gemini-2.0-flash")) {
      return "gemini-2.0-flash";
    }
    return null;
  }

  private async loadInitialState() {
    this.initializing = true;
    this.helperError = null;
    this.helperMessage = null;

    try {
      const [prefsResult, providerResult] = await Promise.all([
        raxApi.getCostPreferences(),
        raxApi.getProviderConfig(),
      ]);

      const preferredModel = prefsResult.success ? prefsResult.data?.preferredModel || null : null;
      const providerModel = providerResult.success ? providerResult.data?.model || null : null;
      const selectedModel = this.normalizeKnownModel(preferredModel || providerModel);
      if (selectedModel) {
        this.selectedModel = selectedModel;
      }

      this.providerSelectionRequired = Boolean(
        providerResult.success &&
        providerResult.data?.selectionRequired &&
        !(preferredModel && preferredModel.trim().length > 0),
      );
    } catch (err) {
      console.warn("[ModelPicker] Could not load initial state:", err);
      this.helperError = "Could not load model preferences.";
    } finally {
      this.initializing = false;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-model-picker": RaxModelPicker;
  }
}
